import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DEMO_LABELS } from "./utils/demographicLabels";
import { getPolConfig } from "./utils/polAggregators";
import {
  loadGroupData,
  DemographicBlock,
  fetchLatestElection,
} from "./utils/metricsClient";
import { getConstituencyBusinessData, getNationalBusinessData } from "./utils/constituencyBusinessData";

/* ─── Tokens ─────────────────────────────────────────────────── */
const INK        = "#0f172a";
const INK2       = "#64748b";
const INK3       = "#94a3b8";
const BORDER     = "#e2e8f0";
const SURF       = "#f8fafc";
const SURF2      = "#f1f5f9";
const STACK_TEAL = "#0abfbc";
const STACK_DARK = "#2c2c2a";
const BLUE       = "#185fa5";
const VIOLET     = "#5b21b6";
const AMBER      = "#92400e";
const POS        = "#15803d";
const NEG        = "#b91c1c";
const POSBG      = "#dcfce7";
const NEGBG      = "#fee2e2";
const NEUBG      = "#f1f5f9";
const HILITE_BG  = "#fff7ed";
const HILITE_BRD = "#fdba74";
const HILITE_MIN_PP = 1.5;
const CARD_TINT = "#fbfdff";

/* ─── Windfall groups ────────────────────────────────────────── */
const WINDFALL_GROUPS = [
  { id: "windfall_banks",         label: "Banks",         groupId: "windfall_banks"         },
  { id: "windfall_hospitality",   label: "Hospitality",   groupId: "windfall_hospitality"   },
  { id: "windfall_construction",  label: "Construction",  groupId: "windfall_construction"  },
  { id: "windfall_defence",       label: "Defence",       groupId: "windfall_defence"       },
  { id: "windfall_gambling",      label: "Gambling",      groupId: "windfall_gambling"      },
  { id: "windfall_manufacturing", label: "Manufacturing", groupId: "windfall_manufacturing" },
  { id: "windfall_online",        label: "Online",        groupId: "windfall_online"        },
  { id: "windfall_propertydev",   label: "Property dev",  groupId: "windfall_propertydev"   },
  { id: "windfall_supermarkets",  label: "Supermarkets",  groupId: "windfall_supermarkets"  },
  { id: "windfall_oil",           label: "Oil & gas",     groupId: "windfall_oil"           },
];

/* ─── Party colour map ───────────────────────────────────────── */
const PARTY_COLORS = {
  "Conservative": "#0087DC", "Labour": "#D50000",
  "Liberal Democrat": "#FDBB30", "Reform UK": "#12B6CF", "Reform": "#12B6CF",
  "Green": "#008066", "SNP": "#FFF95D", "Plaid Cymru": "#3F8428", "Other": "#64748b",
};
const partyCol     = (label) => PARTY_COLORS[label] || STACK_TEAL;
const partyColText = (label) => {
  // Some party colours are too light for white backgrounds — use darker variant for text
  const LIGHT_PARTIES = { "SNP": "#7a6800", "Liberal Democrat": "#b8860b" };
  return LIGHT_PARTIES[label] || PARTY_COLORS[label] || STACK_TEAL;
};

const SlimPill = ({ label, party, share, majority }) => {
  if (!party) return null;
  const col = partyCol(party);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        bgcolor: `${col}20`,
        border: `0.5px solid ${col}`,
        borderRadius: "10px",
        px: "8px",
        py: "4px",
        flexShrink: 0,
        minWidth: 120,
        justifyContent: "flex-start",
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />
      <Box>
        <Typography sx={{ fontSize: 8, fontWeight: 600, color: INK3, lineHeight: 1, mb: "1px" }}>{label}</Typography>
        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, lineHeight: 1, whiteSpace: "nowrap" }}>
          {party}
          {share != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · {Number(share).toFixed(1)}%</Box>}
          {majority != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · Maj {Number(majority).toLocaleString()}</Box>}
        </Typography>
      </Box>
    </Box>
  );
};

/* ─── BusinessTopHeader ─────────────────────────────────────── */
const BusinessTopHeader = ({ constName, areaCode, topVI, national = false }) => {
  const [latestElection, setLatestElection] = React.useState(null);
  const [winner2019, setWinner2019] = React.useState(null);

  React.useEffect(() => {
    if (!areaCode || national) return; // no seat election data at national level
    let cancelled = false;
    fetchLatestElection(areaCode).then((data) => { if (!cancelled) setLatestElection(data); }).catch(() => {});
    const CSV_URL = process.env.PUBLIC_URL + "/static/data/Notional2019Winners_Newpcon.csv";
    fetch(CSV_URL).then((r) => r.text()).then((text) => {
      if (cancelled) return;
      const lines = text.trim().split("\n");
      if (lines.length < 2) return;
      const header = lines[0].replace(/^\uFEFF/, "").split(",");
      for (const line of lines.slice(1)) {
        const cols = line.split(",");
        const row = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
        if (row.new_pcon === areaCode) {
          const shares = {
            Conservative: Number(row.con19), Labour: Number(row.lab19),
            "Liberal Democrat": Number(row.ld19), Green: Number(row.green19),
            Reform: Number(row.brx19), "Plaid Cymru": Number(row.pc19),
            SNP: Number(row.snp19), Other: Number(row.other19),
          };
          // The CSV spells two winners differently from the keys above
          // ("LibDem", "PlaidCymru"), so `shares[row.winner]` was undefined and
          // the pill printed 0.0% — and the wrong colour — in the 11 seats
          // those two won in 2019. Map the label onto the canonical name once,
          // before it is used for either.
          const party2019 = { LibDem: "Liberal Democrat", PlaidCymru: "Plaid Cymru" }[row.winner] || row.winner;
          const party = party2019 || Object.entries(shares).reduce(
            (a, b) => (b[1] > (shares[a] || 0) ? b[0] : a), "Other"
          );
          setWinner2019({ party, share: (shares[party] || 0) * 100 });
          break;
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [areaCode, national]);

  const accent = latestElection?.party
    ? partyCol(latestElection.party)
    : topVI?.label
    ? partyCol(topVI.label)
    : STACK_TEAL;

  return (
    <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
        <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
        <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
          {national ? "National Profile" : "Constituency Profile"}
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: 7.5,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "#1d4ed8",
            background: "rgba(29,78,216,0.08)",
            border: "0.5px solid #c7d2fe",
            px: "6px",
            py: "1.5px",
            borderRadius: "999px",
          }}
        >
          Business Report
        </Box>
        <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", columnGap: "8px", mb: "3px" }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {constName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <SlimPill label="VI Projection" party={topVI?.label} share={topVI?.constituency} />
          <SlimPill label="2024 Result" party={latestElection?.party} share={latestElection?.share} majority={latestElection?.majority} />
          <SlimPill label="2019 Result" party={winner2019?.party} share={winner2019?.share} />
        </Box>
      </Box>
      <Box sx={{ height: 3, width: 180, borderRadius: 4, background: `linear-gradient(90deg, ${accent}, ${accent}55)` }} />
    </Box>
  );
};

/* ─── Helpers ────────────────────────────────────────────────── */
const fmt  = (x) => Number.isFinite(Number(x)) ? `${Number(x).toFixed(1)}%` : "—";
const n    = (x) => Number(x) || 0;
const topN = (arr, k = 5) =>
  (arr || [])
    .filter((d) => Number.isFinite(Number(d?.constituency)))
    .sort((a, b) => Number(b.constituency) - Number(a.constituency))
    .slice(0, k);

function extractLikert(rows) {
  if (!Array.isArray(rows)) return {};
  const find = (suffix) => {
    const row = rows.find((r) => String(r?.key || "").endsWith(suffix));
    return row
      ? { constituency: Number(row.constituency), national: Number(row.national) }
      : { constituency: null, national: null };
  };
  return {
    comfortable:   find(":comfortable:lik"),
    neutral:       find(":neutral:lik"),
    uncomfortable: find(":uncomfortable:lik"),
  };
}
const netC    = (lk) => lk?.comfortable?.constituency != null && lk?.uncomfortable?.constituency != null
  ? n(lk.comfortable.constituency) - n(lk.uncomfortable.constituency) : null;
const netCNat = (lk) => lk?.comfortable?.national != null && lk?.uncomfortable?.national != null
  ? n(lk.comfortable.national) - n(lk.uncomfortable.national) : null;

/* ─── Micro components ───────────────────────────────────────── */

/* Delta badge */
const D = ({ val, nat, sz = 8 }) => {
  const diff = Number.isFinite(n(val) - n(nat)) ? +(n(val) - n(nat)).toFixed(1) : null;
  if (diff == null) return null;
  const up = diff >= 0;
  const zero = diff === 0;
  return (
    <Box component="span" sx={{
      fontSize: sz, fontWeight: 700, borderRadius: "2px",
      px: "3px", py: "0.5px", flexShrink: 0,
      color: zero ? INK2 : up ? POS : NEG,
      bgcolor: zero ? NEUBG : up ? POSBG : NEGBG,
    }}>
      {diff > 0 ? "+" : ""}{diff}pp
    </Box>
  );
};

/* Section label with rule */
const SL = ({ children, color = STACK_TEAL }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "7px" }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: STACK_DARK }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "0.5px", bgcolor: BORDER }} />
  </Box>
);

/* Sub-label */
const Sub = ({ children, color = INK3 }) => (
  <Typography sx={{ fontSize: 7.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color, mb: "5px" }}>
    {children}
  </Typography>
);

/* Divider */
const Div = () => <Box sx={{ height: "0.5px", bgcolor: BORDER, my: "8px" }} />;

/* Card */
const Card = ({ children, sx = {} }) => (
  <Box sx={{ border: `0.5px solid ${BORDER}`, borderRadius: "5px", p: "10px 11px", bgcolor: "#fff", ...sx }}>
    {children}
  </Box>
);

/* Insight callout */
const Insight = ({ eyebrow, headline, sub, implication, accentColor }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderTop: `3px solid ${accentColor}`,
      borderRadius: "10px",
      p: "10px 11px",
      bgcolor: "#fff",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100%",
      boxSizing: "border-box",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}
  >
    <Typography
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: accentColor,
        mb: "4px",
        lineHeight: 1.1,
      }}
    >
      {eyebrow}
    </Typography>

    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 750,
        lineHeight: 1.22,
        color: INK,
        mb: "5px",
        letterSpacing: "-0.01em",
      }}
    >
      {headline}
    </Typography>

    <Typography
      sx={{
        fontSize: 9,
        color: INK2,
        lineHeight: 1.32,
      }}
    >
      {sub}
    </Typography>

    {implication && (
      <Box
        sx={{
          mt: "7px",
          pt: "6px",
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 9.5,
            color: accentColor,
            lineHeight: 1.3,
            fontWeight: 650,
          }}
        >
          {implication}
        </Typography>
      </Box>
    )}
  </Box>
);

/* Clean data row — number + delta only, subtle bottom border */
const Row = ({ rank, label, value, national, color = BLUE, wrapLabel = false, highlight = false, hideNational = false }) => {
  const hasNat = !hideNational && national != null && Number.isFinite(Number(national));
  const diff = hasNat ? +(n(value) - n(national)).toFixed(1) : null;
  const up   = diff != null && diff >= 0;
  return (
    <Box sx={{
      display: "flex",
      alignItems: wrapLabel ? "flex-start" : "baseline",
      gap: "4px",
      py: "4px",
      mx: highlight ? "-4px" : 0,
      px: highlight ? "4px" : 0,
      borderRadius: highlight ? "4px" : 0,
      bgcolor: highlight ? HILITE_BG : "transparent",
      borderBottom: `0.5px solid ${SURF2}`,
      "&:last-child": { borderBottom: "none" },
    }}>
      <Typography sx={{ fontSize: 10, color: INK3, width: 14, flexShrink: 0 }}>{rank}.</Typography>
      <Typography sx={{
        flex: 1, fontSize: 11.5, fontWeight: 500, color: INK,
        overflow: wrapLabel ? "visible" : "hidden",
        textOverflow: wrapLabel ? "clip" : "ellipsis",
        whiteSpace: wrapLabel ? "normal" : "nowrap",
        lineHeight: wrapLabel ? 1.25 : 1,
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK, flexShrink: 0 }}>{fmt(value)}</Typography>
      {diff != null && (
        <Box component="span" sx={{
          fontSize: 9, fontWeight: 700, borderRadius: "2px", px: "3px", py: "0.5px", flexShrink: 0,
          color: diff === 0 ? INK2 : up ? POS : NEG,
          bgcolor: diff === 0 ? NEUBG : up ? POSBG : NEGBG,
        }}>
          {diff > 0 ? "+" : ""}{diff}pp
        </Box>
      )}
    </Box>
  );
};

/* Trust net row — bar from centre */
const TrustRow = ({ label, value, national, maxAbs, highlight = false, hideNational = false }) => {
  const hasNat = !hideNational && national != null && Number.isFinite(Number(national));
  const isPos = value >= 0;
  const pct   = `${Math.min(Math.abs(value) / maxAbs, 1) * 44}%`;
  const natPos = `calc(50% + ${(Math.max(-maxAbs, Math.min(n(national), maxAbs)) / maxAbs) * 44}%)`;
  const diff  = hasNat ? +(n(value) - n(national)).toFixed(1) : null;
  const up    = diff != null && diff >= 0;
  return (
    <Box sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      py: "3.5px",
      mx: highlight ? "-4px" : 0,
      px: highlight ? "4px" : 0,
      borderRadius: highlight ? "4px" : 0,
      bgcolor: highlight ? HILITE_BG : "transparent",
      borderBottom: `0.5px solid ${SURF2}`,
      "&:last-child": { borderBottom: "none" },
    }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 500, color: INK, width: 128, flexShrink: 0, whiteSpace: "normal", lineHeight: 1.18 }}>
        {label}
      </Typography>
      <Box sx={{ width: 150, flexShrink: 0, position: "relative", height: 10, borderRadius: 99, bgcolor: SURF2 }}>
        <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", bgcolor: BORDER, transform: "translateX(-50%)", zIndex: 3 }} />
        <Box sx={{
          position: "absolute", top: "11%", height: "78%", zIndex: 2,
          ...(isPos ? { left: "50%", borderRadius: "0 99px 99px 0" } : { right: "50%", borderRadius: "99px 0 0 99px" }),
          width: pct, bgcolor: isPos ? POS : NEG, opacity: 0.75,
        }} />
        {hasNat && (
          <Box
            sx={{
              position: "absolute",
              left: natPos,
              top: -1,
              bottom: -1,
              width: "1.5px",
              bgcolor: INK3,
              borderRadius: 99,
              transform: "translateX(-50%)",
              zIndex: 4,
            }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, width: 28, textAlign: "right", flexShrink: 0, color: isPos ? POS : NEG }}>
        {value > 0 ? "+" : ""}{value.toFixed(0)}
      </Typography>
      {diff != null && (
        <Box component="span" sx={{
          fontSize: 7.5, fontWeight: 700, borderRadius: "2px", px: "3px", py: "0.5px", width: 32, flexShrink: 0,
          color: diff === 0 ? INK2 : up ? POS : NEG,
          bgcolor: diff === 0 ? NEUBG : up ? POSBG : NEGBG,
        }}>
          {diff > 0 ? "+" : ""}{diff}pp
        </Box>
      )}
    </Box>
  );
};

/* Windfall diverging chart */
const WindfallChart = ({ windfall, fill = false, hideNational = false }) => {
  const rows = useMemo(() =>
    WINDFALL_GROUPS
      .map(({ id, label }) => {
        const lk  = windfall[id] || {};
        const nc  = netC(lk);
        const ncn = netCNat(lk);
        return { id, label, net: nc, netNat: ncn, d: nc != null && ncn != null ? +(nc - ncn).toFixed(1) : null };
      })
      .filter((r) => r.net != null)
      .sort((a, b) => (b.net ?? 0) - (a.net ?? 0)),
  [windfall]);

  if (!rows.length) return <Typography sx={{ fontSize: 9, color: INK3 }}>Loading…</Typography>;

  const maxAbs = Math.max(10, ...rows.map((r) => Math.abs(r.net)), ...rows.map((r) => Math.abs(r.netNat || 0)));
  const maxDiv = Math.max(
    0,
    ...rows.map((r) => (Number.isFinite(r.d) ? Math.abs(r.d) : 0))
  );
  const barW   = (v) => `${(Math.abs(v) / maxAbs) * 44}%`;
  const natPos = (v) => `calc(50% + ${(Math.max(-maxAbs, Math.min(v, maxAbs)) / maxAbs) * 44}%)`;

  return (
    <Box sx={{ height: fill ? "100%" : "auto", display: "flex", flexDirection: "column" }}>
      {/* Legend */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "7px" }}>
        {[{ c: NEG, l: "← Uncomfortable" }, { c: POS, l: "Comfortable →" }].map(({ c, l }) => (
          <Box key={l} sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "2px", bgcolor: c, opacity: 0.75 }} />
            <Typography sx={{ fontSize: 7.5, color: INK2 }}>{l}</Typography>
          </Box>
        ))}
        {!hideNational && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
            <Box sx={{ width: "1.5px", height: 6, bgcolor: INK3 }} />
            <Typography sx={{ fontSize: 7.5, color: INK2 }}>National avg</Typography>
          </Box>
        )}
        <Typography sx={{ ml: "auto", fontSize: 7, color: INK3, fontStyle: "italic" }}>comfortable% − uncomfortable%</Typography>
      </Box>
      {/* Rows */}
      <Box sx={fill ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" } : {}}>
        {rows.map((r) => {
          const isPos = r.net >= 0;
          const up    = r.d != null && r.d >= 0;
          const highlight = maxDiv > HILITE_MIN_PP && Number.isFinite(r.d) && Math.abs(r.d) === maxDiv;
          return (
            <Box
              key={r.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                mb: fill ? 0 : "5px",
                mx: highlight ? "-4px" : 0,
                px: highlight ? "4px" : 0,
                borderRadius: highlight ? "4px" : 0,
                bgcolor: highlight ? HILITE_BG : "transparent",
                outline: highlight ? `0.5px solid ${HILITE_BRD}` : "none",
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 500, color: INK, width: 88, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.label}
              </Typography>
              <Box sx={{ flex: 1, position: "relative", height: 11, borderRadius: 99, bgcolor: SURF2 }}>
                <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px", bgcolor: BORDER, transform: "translateX(-50%)", zIndex: 3 }} />
                <Box sx={{
                  position: "absolute",
                  ...(isPos ? { left: "50%", borderRadius: "0 99px 99px 0" } : { right: "50%", borderRadius: "99px 0 0 99px" }),
                  top: "12%", height: "76%", width: barW(r.net),
                  bgcolor: isPos ? POS : NEG, opacity: 0.75, zIndex: 2,
                }} />
                {!hideNational && r.netNat != null && (
                  <Box sx={{ position: "absolute", left: natPos(r.netNat), top: -1, bottom: -1, width: "1.5px", bgcolor: INK3, borderRadius: 99, transform: "translateX(-50%)", zIndex: 4 }} />
                )}
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, width: 30, textAlign: "right", flexShrink: 0, color: isPos ? POS : NEG }}>
                {r.net > 0 ? "+" : ""}{r.net.toFixed(0)}
              </Typography>
              {!hideNational && r.d != null && (
                <Box component="span" sx={{
                  fontSize: 7.5, fontWeight: 700, borderRadius: "2px", px: "3px", py: "0.5px", width: 32, flexShrink: 0,
                  color: r.d === 0 ? INK2 : up ? POS : NEG,
                  bgcolor: r.d === 0 ? NEUBG : up ? POSBG : NEGBG,
                }}>
                  {r.d > 0 ? "+" : ""}{r.d}pp
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ─── Main component ─────────────────────────────────────────── */
const BusinessReportView = ({ constName, areaCode, height = "1123px", national = false }) => {
  // National scope: fetch national values and map them into the `constituency`
  // slot (with national=null) so all the existing derivations/subcomponents show
  // national figures and auto-hide the "vs national" comparison chrome.
  const remapNat = (arr) => (national ? (arr || []).map((x) => ({ ...x, constituency: x.national ?? null, national: null })) : (arr || []));
  const loadNat = (opts) => loadGroupData({ ...opts, areaCode }).then(remapNat);

  const [challengeData,        setChallengeData]        = useState([]);
  const [trustDriversData,     setTrustDriversData]     = useState([]);
  const [topVI,                setTopVI]                = useState(null);
  const [windfall,             setWindfall]             = useState({});
  const [partySectorList,      setPartySectorList]      = useState([]);
  const [topSectors,           setTopSectors]           = useState({ businesses: [], employees: [] });
  const [sectorImportanceData, setSectorImportanceData] = useState([]);
  const [trustGridData,        setTrustGridData]        = useState([]);
  const nationalIndustry = useMemo(() => getNationalBusinessData(), []);

  /* ── Fetch ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!areaCode) return;
    let active = true;
    (async () => {
      try {
        const [challenge, vi, sectorImportance, trustGrid, trustDrivers] = await Promise.all([
          loadNat({ groupId: "local_business_challenge", level: "constituency" }),
          loadNat({ groupId: "vote_intention",           level: "constituency" }),
          loadNat({ groupId: "sector_importance",        level: "constituency", theme: "ind" }),
          loadNat({ groupId: "trust",                    level: "constituency", theme: "ind" }),
          loadNat({ groupId: "trust_drivers",            level: "constituency", theme: "ind" }),
        ]);
        if (!active) return;
        setChallengeData(challenge || []);

        // Derive top VI for BusinessTopHeader
        const viTop = (vi || [])
          .filter((d) => Number.isFinite(Number(d?.constituency)) && d.key !== "pol:vi:dontknow:cat")
          .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0];
        if (viTop) {
          const VI_CODE_LABEL2 = {
            "pol:vi:conservative:cat": "Conservative", "pol:vi:labour:cat": "Labour",
            "pol:vi:reform:cat": "Reform UK", "pol:vi:libdem:cat": "Liberal Democrat",
            "pol:vi:green:cat": "Green", "pol:vi:snp:cat": "SNP",
            "pol:vi:plaid:cat": "Plaid Cymru", "pol:vi:other:cat": "Other",
          };
          setTopVI({ label: VI_CODE_LABEL2[viTop.key] || viTop.key, constituency: viTop.constituency });
        }

        setSectorImportanceData(sectorImportance || []);
        setTrustGridData(trustGrid || []);
        setTrustDriversData(trustDrivers || []);

        /* Top sector by party voter */
        const VI_KEY_TO_CODE = {
          "pol:vi:conservative:cat": 1, "pol:vi:labour:cat": 2, "pol:vi:reform:cat": 3,
          "pol:vi:libdem:cat": 4, "pol:vi:green:cat": 5, "pol:vi:snp:cat": 6,
          "pol:vi:plaid:cat": 7, "pol:vi:other:cat": 8,
        };
        const VI_CODE_LABEL = {
          1: "Conservative", 2: "Labour", 3: "Reform UK",
          4: "Liberal Democrat", 5: "Green", 6: "SNP", 7: "Plaid Cymru", 8: "Other",
        };
        const topParties = (vi || [])
          .filter((d) => Number.isFinite(Number(d?.constituency)) && VI_KEY_TO_CODE[d.key])
          .sort((a, b) => Number(b.constituency) - Number(a.constituency))
          .slice(0, 3);

        const partyLists = await Promise.all(
          topParties.map(async (p) => {
            const partyCode  = VI_KEY_TO_CODE[p.key];
            const partyLabel = VI_CODE_LABEL[partyCode] || DEMO_LABELS.party?.[partyCode] || p.key;
            const data = await loadNat({
              groupId: "sector_importance",
              level: "constituency", theme: "ind", filters: { party: partyCode },
            });
            const top2 = (data || [])
              .filter((d) => Number.isFinite(Number(d?.constituency)))
              .sort((a, b) => Number(b.constituency) - Number(a.constituency))
              .slice(0, 2);
            return { partyLabel, items: top2 };
          })
        );
        if (!active) return;
        setPartySectorList(partyLists);
      } catch (e) {
        console.error("[BusinessReport]", e);
        if (!active) return;
      }
    })();
    return () => { active = false; };
  }, [areaCode]);

  useEffect(() => {
    if (!constName) return;
    const data = getConstituencyBusinessData(constName);
    setTopSectors({ businesses: data?.businesses || [], employees: data?.employees || [] });
  }, [constName]);

  useEffect(() => {
    if (!areaCode) return;
    let active = true;
    (async () => {
      const results = {};
      await Promise.all(
        WINDFALL_GROUPS.map(async ({ id, groupId }) => {
          try { results[id] = extractLikert(await loadNat({ groupId, level: "constituency" })); }
          catch { results[id] = {}; }
        })
      );
      if (!active) return;
      setWindfall(results);
    })();
    return () => { active = false; };
  }, [areaCode]);

  /* ── Derived ─────────────────────────────────────────────────── */
  const topChallenges   = useMemo(() => topN(challengeData, 3),        [challengeData]);
  const topSectorImport = useMemo(() => topN(sectorImportanceData, 5), [sectorImportanceData]);
  const topTrustDrivers = useMemo(() => topN(trustDriversData, 4),     [trustDriversData]);
  const industryDiffs = useMemo(() => {
    const toMap = (arr) => new Map((arr || []).map((x) => [x.label, x.pct]));
    const natBiz = toMap(nationalIndustry?.businesses);
    const natEmp = toMap(nationalIndustry?.employees);
    const pickBest = (list, natMap) => {
      const diffs = (list || []).map((x) => {
        const nat = natMap.get(x.label) ?? 0;
        return { label: x.label, diff: +(x.pct - nat).toFixed(1), pct: x.pct, nat };
      });
      return diffs.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))[0] || null;
    };
    return {
      businesses: pickBest(topSectors.businesses, natBiz),
      employees: pickBest(topSectors.employees, natEmp),
    };
  }, [topSectors, nationalIndustry]);

  const trustNetData = useMemo(() => {
    const { groupsById } = getPolConfig();
    const trustGroup = groupsById?.trust;
    if (!trustGroup || !Array.isArray(trustGridData)) return [];
    const rowByKey = Object.fromEntries(trustGridData.map((r) => [r.key, r]));
    const sumF = (keys, field) =>
      (Array.isArray(keys) ? keys : [keys]).reduce((acc, k) => acc + (Number(rowByKey[k]?.[field]) || 0), 0);
    return (trustGroup.subVariables || [])
      .map((subId) => {
        const sg = groupsById?.[subId];
        return {
          key:          subId,
          label:        trustGroup.subLabels?.[subId] || sg?.label || subId,
          constituency: sumF(sg?.polarity?.pos, "constituency") - sumF(sg?.polarity?.neg, "constituency"),
          national:     sumF(sg?.polarity?.pos, "national")     - sumF(sg?.polarity?.neg, "national"),
        };
      })
      .filter((d) => Number.isFinite(d.constituency))
      .sort((a, b) => b.constituency - a.constituency);
  }, [trustGridData]);

  const trustMaxAbs = useMemo(
    () => Math.max(10, ...trustNetData.map((r) => Math.abs(r.constituency)), ...trustNetData.map((r) => Math.abs(r.national))),
    [trustNetData]
  );
  const sectorTrustRows = useMemo(() => trustNetData, [trustNetData]);
  const maxSectorImportDiff = useMemo(
    () => Math.max(0, ...topSectorImport.map((r) => Math.abs(n(r.constituency) - n(r.national)))),
    [topSectorImport]
  );
  const maxTrustDriversDiff = useMemo(
    () => Math.max(0, ...topTrustDrivers.map((r) => Math.abs(n(r.constituency) - n(r.national)))),
    [topTrustDrivers]
  );
  const maxTrustNetDiff = useMemo(
    () => Math.max(0, ...sectorTrustRows.map((r) => Math.abs(n(r.constituency) - n(r.national)))),
    [sectorTrustRows]
  );

  /* Insight callout derived values */
  const topImport = topSectorImport[0];
  const wfRows = useMemo(() =>
    WINDFALL_GROUPS
      .map(({ id, label }) => {
        const lk = windfall[id] || {};
        const nc  = netC(lk);
        const ncn = netCNat(lk);
        return { label, net: nc, netNat: ncn, d: nc != null && ncn != null ? +(nc - ncn).toFixed(1) : null };
      })
      .filter((r) => r.net != null)
      .sort((a, b) => (b.net ?? 0) - (a.net ?? 0)),
  [windfall]);
  const topWf     = wfRows[0] || null;
  const bottomWf  = wfRows[wfRows.length - 1] || null;
  const windfallGap = useMemo(() =>
    [...wfRows].sort((a, b) => Math.abs(b.d ?? 0) - Math.abs(a.d ?? 0))[0] || null,
  [wfRows]);
  const bestTrust = trustNetData[0] || null;
  const standoutTrust = useMemo(() =>
    [...trustNetData].sort((a, b) => Math.abs(b.constituency - b.national) - Math.abs(a.constituency - a.national))[0],
  [trustNetData]);

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <Box sx={{
      backgroundColor: "#fff",
      color: INK,
      width: "794px",
      height,
      mx: "auto",
      px: "22px",
      pt: "16px",
      pb: "14px",
      fontFamily: "'Inter', system-ui, sans-serif",
      display: "flex",
      flexDirection: "column",
      gap: "9px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
    }}>

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <BusinessTopHeader constName={constName} areaCode={areaCode} topVI={topVI} national={national} />

      {/* ── DEMOGRAPHIC CONTEXT BANNER ────────────────────────── */}
      <Box sx={{
        border: `0.5px solid ${BORDER}`,
        borderLeft: `3px solid ${STACK_TEAL}`,
        borderRadius: "5px",
        px: "10px",
        py: "7px",
        display: "flex",
        gap: "10px",
        alignItems: "center",
        background: SURF,
      }}>
        <Typography sx={{ fontSize: 7.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: INK3, minWidth: 100, flexShrink: 0 }}>
          {national ? "National profile" : "Constituency profile"}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <DemographicBlock constCode={areaCode} constName={constName} variant="blurb" showFields={false} national={national} />
        </Box>
      </Box>

      {/* ── INDUSTRY BREAKDOWN (TOP ROW) ───────────────────────── */}
      <Card>
        <SL color={STACK_TEAL}>Industry breakdown</SL>
        <Typography sx={{ fontSize: 9.5, color: INK2, mb: "6px", lineHeight: 1.5 }}>
          {national ? (
            `The largest industries across Great Britain, by number of businesses and by employment.`
          ) : (() => {
            const biz = industryDiffs?.businesses;
            const emp = industryDiffs?.employees;
            const highlights = [];
            if (biz?.label && Number.isFinite(biz.diff) && biz.diff > 0) {
              highlights.push(`${biz.label} businesses +${biz.diff.toFixed(1)}pp`);
            }
            if (emp?.label && Number.isFinite(emp.diff) && emp.diff > 0) {
              highlights.push(`${emp.label} employment +${emp.diff.toFixed(1)}pp`);
            }
            return highlights.length
              ? `Looking at industries in ${constName}, we see more ${highlights.join(" and more ")} than the national share.`
              : `Looking at industries in ${constName}, no top industry segment is above national share.`;
          })()}
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
          {[
            { key: "businesses", label: "By businesses", color: VIOLET },
            { key: "employees",  label: "By employees",  color: STACK_TEAL },
          ].map(({ key, label, color }) => (
            <Box key={key}>
              <Sub color={color}>{label}</Sub>
              {((national ? nationalIndustry : topSectors)[key] || []).slice(0, 3).length === 0
                ? <Typography sx={{ fontSize: 9, color: INK3 }}>No data.</Typography>
                : ((national ? nationalIndustry : topSectors)[key] || []).slice(0, 3).map((s, i) => (
                  <Box key={`${s.label}-${i}`} sx={{
                    display: "flex", alignItems: "baseline", gap: "4px",
                    py: "3.5px", borderBottom: `0.5px solid ${SURF2}`,
                    "&:last-child": { borderBottom: "none" },
                  }}>
                    <Typography sx={{ fontSize: 10, color: INK3, width: 14, flexShrink: 0 }}>{i + 1}.</Typography>
                    <Typography sx={{ flex: 1, fontSize: 10.5, fontWeight: 500, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color, flexShrink: 0 }}>{s.pct}%</Typography>
                  </Box>
                ))
              }
            </Box>
          ))}
        </Box>
      </Card>

      {/* ── INSIGHT CALLOUTS ───────────────────────────────────── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "7px" }}>
        {national ? (
          <>
            <Insight
              eyebrow="Top sector"
              accentColor={STACK_TEAL}
              headline={topImport ? `${topImport.label} is the most important sector nationally` : "Loading…"}
              sub={topImport ? `${fmt(topImport.constituency)} rank it a leading economic priority across Great Britain.` : ""}
              implication="Use as the national benchmark when comparing individual constituencies."
            />
            <Insight
              eyebrow="Windfall exposure"
              accentColor={AMBER}
              headline={topWf ? `${topWf.label} sees the highest windfall-tax comfort` : "Loading…"}
              sub={topWf ? `Net comfort ${topWf.net > 0 ? "+" : ""}${topWf.net?.toFixed(0)} nationally${bottomWf ? `; lowest for ${bottomWf.label} (${bottomWf.net?.toFixed(0)})` : ""}.` : ""}
              implication="Sectors with negative net comfort carry the greatest windfall-tax backlash risk."
            />
            <Insight
              eyebrow="Trust landscape"
              accentColor={VIOLET}
              headline={bestTrust ? `${bestTrust.label} is the most trusted sector nationally` : "Loading…"}
              sub={bestTrust ? `Highest net trust across GB (${bestTrust.constituency > 0 ? "+" : ""}${Number(bestTrust.constituency).toFixed(0)}).` : ""}
              implication="Low-trust sectors need credibility-building before policy asks."
            />
          </>
        ) : (
        <>
        <Insight
          eyebrow="Sector sentiment"
          accentColor={STACK_TEAL}
          headline={(() => {
            if (!topImport) return "Loading…";
            const d = n(topImport.constituency) - n(topImport.national);
            if (Math.abs(d) < 1) return `${topImport.label} is the top economic priority here`;
            return `Support for ${topImport.label} is ${d > 0 ? "notably stronger" : "weaker"} than nationally`;
          })()}
          sub={(() => {
            if (!topImport) return "";
            const d = +(n(topImport.constituency) - n(topImport.national)).toFixed(1);
            const abs = Math.abs(d);
            if (abs < 1) return `${fmt(topImport.constituency)} local vs ${fmt(topImport.national)} national.`;
            return `${fmt(topImport.constituency)} local vs ${fmt(topImport.national)} national (${d > 0 ? "+" : "-"}${abs}pp).`;
          })()}
          implication={(() => {
            if (!topImport) return null;
            const d = +(n(topImport.constituency) - n(topImport.national)).toFixed(1);
            const label = topImport.label;
            if (d > 3) return `Strong mandate for ${label}. Lead with jobs and local investment.`;
            if (d > 0) return `Supportive local context for ${label}. Engage the MP on growth and employment.`;
            if (d < -3) return `Weak local mandate for ${label}. Lead with local impact, not sector identity.`;
            return `Neutral local context for ${label}. Standard framing should work.`;
          })()}
        />
        <Insight
          eyebrow="Windfall exposure"
          accentColor={AMBER}
          headline={(() => {
            if (!windfallGap && !topWf) return "Loading…";
            // Lead with resistance if there's a negative-net sector — more actionable for PA
            if (bottomWf?.net != null && bottomWf.net < -10)
              return `Local resistance to taxing ${bottomWf.label} is a significant political factor`;
            if (windfallGap?.label && windfallGap?.d != null) {
              const dir = windfallGap.d > 0 ? "more" : "less";
              return `${windfallGap.label} shows the biggest local swing on windfall taxes`;
            }
            return `${topWf?.label} has the highest windfall tax comfort here`;
          })()}
          sub={(() => {
            if (bottomWf?.net != null && bottomWf.net < -10) {
              const natGap = bottomWf.d != null ? `, ${Math.abs(bottomWf.d).toFixed(1)}pp ${bottomWf.d < 0 ? "below" : "above"} national` : "";
              return `${bottomWf.label} net comfort: ${bottomWf.net.toFixed(0)}${natGap}.`;
            }
            if (windfallGap?.label && windfallGap?.d != null) {
              const abs = Math.abs(windfallGap.d).toFixed(1);
              return `${windfallGap.label}: ${abs}pp ${windfallGap.d > 0 ? "more" : "less"} comfortable than national.`;
            }
            if (!topWf) return "";
            return `${topWf.label} net comfort ${topWf.net > 0 ? "+" : ""}${topWf.net?.toFixed(0)}.`;
          })()}
          implication={(() => {
            if (bottomWf?.net != null && bottomWf.net < -10)
              return `High backlash risk on taxing ${bottomWf.label}. Test language before MP engagement.`;
            if (windfallGap?.d != null && windfallGap.d > 5)
              return `Lower backlash risk on a windfall stance for ${windfallGap.label}.`;
            if (windfallGap?.d != null && windfallGap.d < -5)
              return `High sensitivity on taxing ${windfallGap.label}. Use cautious framing.`;
            return `Sentiment is close to national. Limited local differentiation.`;
          })()}
        />
        <Insight
          eyebrow="Trust landscape"
          accentColor={VIOLET}
          headline={(() => {
            if (!standoutTrust) return "Loading…";
            const d = +(standoutTrust.constituency - standoutTrust.national).toFixed(1);
            if (d > 0) return `${standoutTrust.label} enjoys unusually strong local trust`;
            return `${standoutTrust.label} faces a significant local trust deficit`;
          })()}
          sub={(() => {
            if (!standoutTrust) return "";
            const sDelta = +(standoutTrust.constituency - standoutTrust.national).toFixed(1);
            const dir = sDelta > 0 ? "higher" : "lower";
            return `Net trust is ${Math.abs(sDelta).toFixed(1)}pp ${dir} than national.`;
          })()}
          implication={(() => {
            if (!standoutTrust) return null;
            const d = +(standoutTrust.constituency - standoutTrust.national).toFixed(1);
            const label = standoutTrust.label;
            if (d > 10) return `Trust tailwind for ${label}. Push proactive policy asks.`;
            if (d > 0) return `Trust edge for ${label}. Lean into local proof points.`;
            if (d < -10) return `Trust deficit for ${label}. Rebuild credibility before policy asks.`;
            return `Slight trust drag for ${label}. Use evidence-led local messaging.`;
          })()}
        />
        </>
        )}
      </Box>

      {/* ══ MAIN GRID — swapped: left (sector + party + windfall) | right (trust drivers + sector trust) ══ */}
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.1fr)", gap: "9px", flex: 1, minHeight: 0, alignItems: "stretch" }}>

        {/* LEFT COLUMN */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "9px", minHeight: 0 }}>
          <Card>

            {/* Sector importance — clean number rows */}
            <SL color={BLUE}>Sector importance</SL>
            <Typography sx={{ fontSize: 9.5, color: INK2, mb: "6px", lineHeight: 1.4 }}>
              {national ? "How the country ranks sectors by economic importance." : "How constituents rank sectors by economic importance."}
            </Typography>
            {topSectorImport.map((item, i) => (
              <Row key={item.key || i} rank={i + 1} label={item.label}
                value={item.constituency}
                national={item.national}
                color={item.color || BLUE}
                hideNational={national}
                highlight={!national && maxSectorImportDiff > HILITE_MIN_PP && Math.abs(n(item.constituency) - n(item.national)) === maxSectorImportDiff}
              />
            ))}

            <Div />

            {/* Party sector cards — directly below importance */}
            {partySectorList.filter(p => p.items?.length).length > 0 && (
              <>
                <Sub>Top sector by party voter</Sub>
                <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(partySectorList.filter(p => p.items?.length).length, 3)}, minmax(0, 1fr))`, gap: "5px" }}>
                  {partySectorList.filter(p => p.items?.length).map(({ partyLabel, items }) => {
                    const col = partyCol(partyLabel);
                    const d   = items[0]?.constituency != null && items[0]?.national != null
                      ? +(n(items[0].constituency) - n(items[0].national)).toFixed(1) : null;
                    return (
                      <Box key={partyLabel} sx={{
                        minWidth: 0, border: `0.5px solid ${BORDER}`, borderTop: `2px solid ${col}`,
                        borderRadius: "4px", px: "8px", py: "6px", bgcolor: SURF,
                      }}>
                        <Typography sx={{ fontSize: 8, fontWeight: 700, color: partyColText(partyLabel), mb: "2px" }}>{partyLabel}</Typography>
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: INK, whiteSpace: "normal", lineHeight: 1.2 }}>
                          {items[0]?.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Card>

          {/* Windfall — swapped to left and stretched */}
          <Card sx={{ flex: 2, minHeight: 0, bgcolor: CARD_TINT, display: "flex", flexDirection: "column" }}>
            <SL color={AMBER}>Windfall tax - net comfort by sector</SL>
            <Typography sx={{ fontSize: 9.5, color: INK2, mb: "6px", lineHeight: 1.4 }}>
              {national
                ? "Most and least accepted sectors for windfall taxation across Great Britain."
                : "Most and least accepted sectors for windfall taxation in the constituency."}
            </Typography>
            <Typography sx={{ fontSize: 8.2, color: INK2, mb: "6px", lineHeight: 1.35 }}>
              {national
                ? "Sectors ordered by net comfort (comfortable minus uncomfortable) across Great Britain."
                : "Rows are ordered by local net comfort (comfortable minus uncomfortable). The grey tick marks the national net comfort for each sector; gaps from the bar to the tick show local divergence. Highlighted rows mark the largest local-vs-national gap."}
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <WindfallChart windfall={windfall} fill hideNational={national} />
            </Box>
          </Card>
        </Box>

        {/* RIGHT COLUMN */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "9px", minHeight: 0 }}>

          
          <Card sx={{ flex: 1.75, minHeight: 0, bgcolor: CARD_TINT, display: "flex", flexDirection: "column" }}>
            <SL color={VIOLET}>Sector trust - net score</SL>
            <Typography sx={{ fontSize: 9.5, color: INK2, mb: "6px", lineHeight: 1.4 }}>
              {national ? "Most trusted sectors across Great Britain." : "Most trusted sectors in the constituency."}
            </Typography>
            <Typography sx={{ fontSize: 8.2, color: INK2, mb: "6px", lineHeight: 1.35 }}>
              {national
                ? "Sectors ordered by net trust (positive minus negative) across Great Britain."
                : "Rows are ordered by local net trust (positive minus negative). The grey tick marks the national net trust for each sector; gaps from the bar to the tick show local divergence. Highlighted rows mark the largest local-vs-national gap."}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "7px" }}>
              {[{ c: NEG, l: "← Lower trust" }, { c: POS, l: "Higher trust →" }].map(({ c, l }) => (
                <Box key={l} sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "2px", bgcolor: c, opacity: 0.75 }} />
                  <Typography sx={{ fontSize: 7.5, color: INK2 }}>{l}</Typography>
                </Box>
              ))}
              {!national && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                  <Box sx={{ width: "1.5px", height: 6, bgcolor: INK3 }} />
                  <Typography sx={{ fontSize: 7.5, color: INK2 }}>National avg</Typography>
                </Box>
              )}
              <Typography sx={{ ml: "auto", fontSize: 7, color: INK3, fontStyle: "italic" }}>
                net trust = positive - negative
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              {sectorTrustRows.map((item) => (
                <TrustRow key={item.key} label={item.label}
                  value={item.constituency}
                  national={item.national}
                  maxAbs={trustMaxAbs}
                  hideNational={national}
                  highlight={!national && maxTrustNetDiff > HILITE_MIN_PP && Math.abs(n(item.constituency) - n(item.national)) === maxTrustNetDiff}
                />
              ))}
            </Box>
          </Card>

          {/* Trust drivers — now stretched below */}
          <Card sx={{ flex: 0.7, minHeight: 0 }}>
            <SL color={STACK_TEAL}>Trust drivers</SL>
            <Typography sx={{ fontSize: 9, color: INK2, mb: "5px", lineHeight: 1.3 }}>
              {national ? "What people nationally say makes businesses trustworthy." : "What local constituents say makes businesses trustworthy."}
            </Typography>
            {topTrustDrivers.map((item, i) => (
              <Row key={item.key || i} rank={i + 1} label={item.label}
                value={item.constituency}
                national={item.national}
                color={STACK_TEAL}
                wrapLabel
                hideNational={national}
                highlight={!national && maxTrustDriversDiff > HILITE_MIN_PP && Math.abs(n(item.constituency) - n(item.national)) === maxTrustDriversDiff}
              />
            ))}
          </Card>
        </Box>
      </Box>


    </Box>
  );
};

export default BusinessReportView;
